import _ from 'underscore';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { companySizeEnumToString } from './company-size-mapper';

const typeMapping = {
  '#Model.AgeCriterion': 'Age',
  '#Model.GenderCriterion': 'Gender',
  '#Model.DeviceCriterion': 'Device',
  '#Model.LocationIntentCriterion': 'Location',
  '#Model.NegativeLocationCriterion': 'NegativeLocation',
  '#Model.RadiusCriterion': 'Radius',
  '#Model.CompanyNameCriterion': 'CompanyName',
  '#Model.IndustryCriterion': 'Industry',
  '#Model.JobFunctionCriterion': 'JobFunction',
  '#Model.AudienceCriterion': 'Audience',
  '#Model.ContextualTarget': 'ContextualTarget',
  '#Model.PlacementCriterion': 'Placement',
};

/**
 * When the fetched criterion was removed and then added back
 * the ID is lost. Thus, we need to compare if two criterions are save by comparing the target.
 *
 * @param {Array} criterionList - list of criterions
 * @param {Object} targetCriterion - criterion to find.
 * @returns {Object} - existing crterion if there is one.
 */
const findSameCriterionWithoutId = (criterionList, targetCriterion) => {
  const filteredByType = _.filter(
    criterionList,
    criterion => criterion.CriterionType === targetCriterion.CriterionType
  );

  let matchingCriterion;

  // TODO: (youngim) Needs to add other types when MT supports them.
  _.each(filteredByType, (criterion) => {
    switch (targetCriterion.Criterion.Type) {
      case 'Age':
        if (criterion.Criterion.AgeCriterion.Target ===
          targetCriterion.Criterion.AgeCriterion.Target) {
          matchingCriterion = criterion;
        }
        break;

      case 'Gender':
        if (criterion.Criterion.GenderCriterion.Target ===
          targetCriterion.Criterion.GenderCriterion.Target) {
          matchingCriterion = criterion;
        }
        break;

      case 'Device':
        if (criterion.Criterion.DeviceCriterion.DeviceName ===
          targetCriterion.Criterion.DeviceCriterion.DeviceName) {
          matchingCriterion = criterion;
        }
        break;

      case 'CompanyName':
        if ((criterion.CriterionType === targetCriterion.CriterionType) &&
          (criterion.Criterion.CompanyNameCriterion.Target ===
            targetCriterion.Criterion.CompanyNameCriterion.Target)) {
          matchingCriterion = criterion;
        }
        break;

      case 'Industry':
        if ((criterion.CriterionType === targetCriterion.CriterionType) &&
          (criterion.Criterion.IndustryCriterion.Target ===
            targetCriterion.Criterion.IndustryCriterion.Target)) {
          matchingCriterion = criterion;
        }
        break;

      case 'JobFunction':
        if ((criterion.CriterionType === targetCriterion.CriterionType) &&
          (criterion.Criterion.JobFunctionCriterion.Target ===
            targetCriterion.Criterion.JobFunctionCriterion.Target)) {
          matchingCriterion = criterion;
        }
        break;

      case 'Audience':
        if ((criterion.CriterionType === targetCriterion.CriterionType) &&
          (criterion.Criterion.AudienceId ===
            targetCriterion.Criterion.AudienceId)) {
          matchingCriterion = criterion;
        }
        break;

      default:
        break;
    }
  });

  return matchingCriterion;
};

const criterionHelper = {
  /**
   * This method compares and figure out what to save to the MT.
   * Criterion can be either
   * - Newly added
   * - Removed and added back
   * - Modified
   * - Deleted
   * @param {Array} originalCriterions - criterions to compare
   * @param {Array} modifiedCriterions - criterions to compare after modify
   * @param {boolean} fromCreationView - indicate if fromCreationView
   *
   * @returns {Array} - array of criterions to save
   */
  compareAndGetCriterionsDelta: (
    originalCriterions, modifiedCriterions,
    fromCreationView = false
  ) => {
    const criterionsToReturn = [];

    _.each(modifiedCriterions, (criterion) => {
      // Handle criterion change that is saved in MT
      if (criterion.Id > 0) {
        const originalCriterion = _.find(originalCriterions, c => c.Id === criterion.Id);

        // if changed, push the updated new criterion
        if (!_.isEqual(originalCriterion, criterion) &&
          (!fromCreationView || (fromCreationView && criterion.LevelOfDetails !== 'Campaign'))) {
          criterionsToReturn.push(criterion);
        }
      } else {
        // Need to check if this is newly added or added back after removal.
        const existingCriterion =
          findSameCriterionWithoutId(originalCriterions, criterion);

        if (existingCriterion) {
          // This was existing but removed and then added back.
          const criterionToAdd = cloneDeep(existingCriterion);

          // Copy over the bid adjustment
          criterionToAdd.Criterion.BidAdjustment = criterion.Criterion.BidAdjustment;

          // now check if is changed, if so push the updated new criterion
          if (!_.isEqual(criterionToAdd, existingCriterion)) {
            criterionsToReturn.push(criterionToAdd);
          } else {
            // They are same. Give modified one the original ID.
            // eslint-disable-next-line no-param-reassign
            criterion.Id = existingCriterion.Id;
          }
        } else {
          // It is newly aded, just add.
          criterionsToReturn.push(criterion);
        }
      }
    });

    // Find deleted criterions
    _.each(originalCriterions, (criterion) => {
      // If unmodified criterion cannot be found in the newlist
      if (!_.find(modifiedCriterions, c => c.Id === criterion.Id) &&
        !_.find(criterionsToReturn, c => c.Id === criterion.Id)) {
        // Mark the criterion as delete
        const criterionToDelete = cloneDeep(criterion);

        criterionToDelete.Status = 'Deleted';

        criterionsToReturn.push(criterionToDelete);
      }
    });

    return criterionsToReturn;
  },

  /**
   * This method applies the delta to to the list
   * Criterion can be either
   * - Newly added
   * - Deleted
   * @param {Array} originalCriterions - criterions before apply delta
   * @param {Array} deltaCriterions - criterions of delta
   *
   * @returns {Array} - array of criterions to save
   */
  applyCriterionsDelta: (originalCriterions, deltaCriterions) => {
    let criterionsToReturn = cloneDeep(originalCriterions);

    _.each(deltaCriterions, (deltaCriterion) => {
      // The original criterion should already have this.
      if (deltaCriterion.Status === 'Deleted') {
        criterionsToReturn = _.reject(
          criterionsToReturn,
          criterion => criterion.Id === deltaCriterion.Id
        );
      } else {
        // created, simply push
        criterionsToReturn.push(deltaCriterion);
      }
    });

    return criterionsToReturn;
  },

  reformatFetchedCriterion: (fetchedCriterion, campaignId, adGroupId) => {
    const formatedCriterion = cloneDeep(fetchedCriterion);

    // Set Ids
    if (adGroupId) {
      formatedCriterion.AdGroup = {
        Id: adGroupId,
      };
    } else {
      formatedCriterion.Campaign = {
        Id: campaignId,
      };
    }


    // Set CriterionType
    if (_.has(typeMapping, formatedCriterion.Criterion['@odata.type'])) {
      formatedCriterion.CriterionType =
        typeMapping[formatedCriterion.Criterion['@odata.type']];

      if (formatedCriterion.CriterionType === 'ContextualTarget') {
        formatedCriterion.Criterion.Type = 'ContextualTarget';
      }

      if (formatedCriterion['@odata.type'] === '#Model.NegativeAdGroupCriterion') {
        formatedCriterion.CriterionType = `Negative${formatedCriterion.CriterionType}`;
      }
    }

    // Seems we no longer is #Model.NegativeLocationCriterion for excluded locations
    // In the actual result, it returns as below:
    if (formatedCriterion.Criterion['@odata.type'] === '#Model.LocationCriterion' &&
      formatedCriterion['@odata.type'] && formatedCriterion['@odata.type'].includes('#Model.Negative')) {
      formatedCriterion.CriterionType = 'NegativeLocation';
    }

    // Format target type
    switch (formatedCriterion.Criterion.Type) {
      case 'Age': {
        const target = formatedCriterion.Criterion.AgeRange;

        delete formatedCriterion.Criterion.AgeRange;

        formatedCriterion.Criterion.AgeCriterion = {
          Target: target,
        };

        break;
      }


      case 'Gender': {
        const target = formatedCriterion.Criterion.GenderType;

        delete formatedCriterion.Criterion.GenderType;

        formatedCriterion.Criterion.GenderCriterion = {
          Target: target,
        };

        break;
      }

      case 'Device': {
        const target = formatedCriterion.Criterion.DeviceName;

        delete formatedCriterion.Criterion.DeviceName;

        formatedCriterion.Criterion.DeviceCriterion = {
          DeviceName: target,
        };

        break;
      }

      case 'CompanyName': {
        const target = formatedCriterion.Criterion.ProfileId;

        delete formatedCriterion.Criterion.ProfileId;

        formatedCriterion.Criterion.CompanyNameCriterion = {
          Target: target,
          Name: formatedCriterion.Criterion.CompanyName,
          LogoUrl: formatedCriterion.Criterion.LogoUrl,
          Industry: formatedCriterion.Criterion.Industry,
          Size: companySizeEnumToString(formatedCriterion.Criterion.Size),
        };

        break;
      }

      case 'Industry': {
        const target = formatedCriterion.Criterion.ProfileId;

        delete formatedCriterion.Criterion.ProfileId;

        formatedCriterion.Criterion.IndustryCriterion = {
          Target: target,
          Name: formatedCriterion.Criterion.Industry,
          IndustryGroupName: formatedCriterion.Criterion.IndustryGroupName,
        };

        break;
      }

      case 'JobFunction': {
        const target = formatedCriterion.Criterion.ProfileId;

        delete formatedCriterion.Criterion.ProfileId;

        formatedCriterion.Criterion.JobFunctionCriterion = {
          Target: target,
          Name: formatedCriterion.Criterion.JobFunction,
        };

        break;
      }

      default:
        break;
    }

    // Format BidAdjustmnet
    let bidValue = 0;

    if (formatedCriterion.CriterionBid) {
      bidValue = formatedCriterion.CriterionBid.Multiplier;

      delete formatedCriterion.CriterionBid;
    }

    if (fetchedCriterion.constructor && fetchedCriterion.constructor.name === 'CampaignCriterion') {
      formatedCriterion.LevelOfDetails = 'Campaign';
    } else if (fetchedCriterion.constructor && fetchedCriterion.constructor.name === 'AdGroupCriterion') {
      formatedCriterion.LevelOfDetails = 'AdGroup';
    }

    formatedCriterion.Criterion.BidAdjustment = bidValue;

    let cashbackPercent = null;
    if (formatedCriterion.CriterionCashback) {
      cashbackPercent = formatedCriterion.CriterionCashback.CashbackPercent;

      delete formatedCriterion.CriterionCashback;
    }

    formatedCriterion.Criterion.CashbackPercent = cashbackPercent;

    return formatedCriterion;
  },

  /**
   * Utility function for building CompanyName/Industry/JobFunction criterion
   * @private
   *
   * @param {string} type - criterionType, one of CompanyName/Industry/JobFunction
   * @param {Object} targetItem - item for the criterion
   * @param {bool} isIncluded - if it is included or not
   * @param {Number} bidAdjustment - bid adjustment
   * @param {Number} cashbackPercent - cashback percent
   * @returns {Object} - criterion
   */
  buildLinkedInCriterion: (type, targetItem, isIncluded, bidAdjustment, cashbackPercent) => {
    const criterionName = `${type}Criterion`;
    const targetName = targetItem.name || targetItem.Name;
    const targetId = targetItem.id || targetItem.Id;
    const criterion = {
      Type: type,
      BidAdjustment: bidAdjustment,
      CashbackPercent: cashbackPercent,
      Name: targetName,
      [criterionName]: {
        Target: targetId,
        Name: targetName,
      },
    };

    const criterionAttrs = type === 'Industry' ? {
      [`${type}GroupName`]: targetItem[`${type}GroupName`],
    } : {
      LogoUrl: targetItem.logoUrl || targetItem.LogoUrl,
      Industry: targetItem.industry || targetItem.Industry,
      Size: targetItem.size || targetItem.Size,
    };

    _.extend(criterion[criterionName], criterionAttrs);

    const criterionEntity = {
      CriterionType: isIncluded ? type : `Negative${type}`,
      Criterion: criterion,
    };

    return criterionEntity;
  },

};

export { criterionHelper, typeMapping };
